<template>
  <div class="group-accred-information">
    <slot name="title"/>
    <slot name="descriptions"/>
    <div class="group-accred-information_content">
      <CollapseBlock
        transparent
        :value.sync="show.info">
        <div
          slot="title"
          class="group-accred-information__collapse-title mt-0">
          {{ $t('groupOfAccreditation.groupInformation') }}
          <sIcon
            class="ml-2 my-auto"
            :rotate="show.info ? '-180' : '0'"
            size="14"
            type="arrow"/>
        </div>
        <sFormItem
          v-if="showOrganizations"
          class="group-accred-information__content-item mb-6"
          :label="$t('Tenders.TenderOrganizer')"
          prop="organizationIds">
          <sSelect
            class="width-100"
            clearable
            data-qa="group-accred-organizations"
            :disabled="disabled"
            :filter-method="filterOrganizations"
            filterable
            multiple
            :placeholder="$t('Common.Select')"
            :value="organizationIds"
            @update:value="organizationIds = $event">
            <sOption
              v-for="organization in filteredOrganizations"
              :key="`group-accred-organization-${$_accreditationOrganizationKey(organization)}`"
              :data-qa="`group-accred-organization-${$_accreditationOrganizationKey(organization)}`"
              :label="organization.title"
              :value="$_accreditationOrganizationKey(organization)">
              <div>
                <div>{{ organization.title }}</div>
                <div
                  v-if="organization.oKPO"
                  class="font-size-12 opacity-8">
                  {{ $t('Tenders.EDRPOU') }}: {{ organization.oKPO }}
                </div>
              </div>
            </sOption>
          </sSelect>
        </sFormItem>
        <sFormItem
          class="group-accred-information__content-item mb-6"
          :label="$t('Planning.Name')"
          prop="title">
          <template v-if="loading">
            <v-skeleton-loader
              :height="40"
              loading
              type="heading"
              width="100%"/>
          </template>
          <sInput
            v-else-if="itemMutable"
            clearable
            data-qa="title"
            :disabled="disabled"
            max-length="254"
            :placeholder="$t('groupOfAccreditation.enterName')"
            show-word-limit
            :value.sync="itemMutable.title"/>
        </sFormItem>
        <sFormItem
          class="group-accred-information__content-item mb-6"
          :label="$t('Common.Description')"
          prop="description">
          <template v-if="loading">
            <v-skeleton-loader
              :height="40"
              loading
              type="image"
              width="100%"/>
          </template>
          <sInput
            v-else-if="itemMutable"
            clearable
            data-qa="description"
            :disabled="disabled"
            :max-length="4000"
            max-rows="20"
            :placeholder="$t('groupOfAccreditation.enterDescription')"
            rows="4"
            show-word-limit
            type="textarea"
            :value.sync="itemMutable.description"/>
        </sFormItem>
        <sFormItem
          v-if="growthBook.show_isDocumentsSigningRequired"
          prop="isDocumentsSigningRequired">
          <div
            slot="label"
            class="h5-headline">
            {{ $t('groupOfAccreditation.requireDocumentSigning') }}
          </div>
          <sRadioGroup
            v-model="itemMutable.isDocumentsSigningRequired"
            class="mt-2"
            data-qa="isDocumentsSigningRequired"
            row>
            <sRadio
              data-qa="isDocumentsSigningRequired-with"
              :label="true">
              {{ $t('Tenders.Yes') }}
            </sRadio>
            <sRadio
              data-qa="isDocumentsSigningRequired-without"
              :label="false">
              {{ $t('Tenders.No') }}
            </sRadio>
          </sRadioGroup>
        </sFormItem>
        <sFormItem
          v-if="$can('show', 'allowAccreditationTestGroups') && itemMutable"
          class="group-accred-information__content-item mb-6"
          prop="isTest">
          <div
            slot="label"
            class="h5-headline">
            {{ $t('groupOfAccreditation.testGroup') }}
          </div>
          <sRadioGroup
            v-model="itemMutable.isTest"
            class="mt-2"
            data-qa="isTest"
            :disabled="disabled || (item.status && itemMutable && itemMutable.status !== 'DRAFT')"
            row>
            <sRadio
              data-qa="isTest-with"
              :label="true">
              {{ $t('Tenders.Yes') }}
            </sRadio>
            <sRadio
              data-qa="isTest-without"
              :label="false">
              {{ $t('Tenders.No') }}
            </sRadio>
          </sRadioGroup>
        </sFormItem>
      </CollapseBlock>

      <CollapseBlock
        transparent
        :value.sync="show.classificationCode">
        <div
          slot="title"
          class="group-accred-information__collapse-title mt-0">
          {{ $t('Tenders.Categories') | firstUpperCase }}
          <sIcon
            class="ml-2 my-auto"
            :rotate="show.classificationCode ? '-180' : '0'"
            size="14"
            type="arrow"/>
        </div>
        <sFormItem
          ref="formClassificationCode"
          class="group-accred-information__content-item mb-6"
          prop="categories"
          :show-message="false">
          <template #default="{ error, state }">
            <div
              class="max-height-200 overflow-auto"
              data-qa="classifications">
              <div
                v-if="loading"
                class="my-4">
                <v-skeleton-loader
                  ref="skeleton"
                  :boilerplate="false"
                  class="main__green--text"
                  :tile="false"
                  type="sentences"
                  width="100%"/>
                <v-skeleton-loader
                  :height="40"
                  loading
                  type="sentences"
                  width="100%"/>

                <v-skeleton-loader
                  :height="40"
                  loading
                  type="heading"/>
              </div>
              <div
                v-else-if="categories && categories.length"
                style="min-height: 20px; max-height: 200px; overflow: auto;">
                <div
                  v-for="(code, index) in categories"
                  :key="index"
                  class="flex"
                  :data-qa="`classification-item-${index}`">
                  <div class="my-auto mr-1 classification-item_title">
                    {{ code.title }}
                  </div>
                  <sButton
                    :disabled="disabled"
                    icon="cross"
                    small
                    type="text"
                    @click="deleteClassificationCode(index)"/>
                </div>
              </div>
              <CategoryDropdownTree
                v-if="!loading"
                ref="categoryTree"
                class="v-input"
                data-qa="classification"
                :disable="loading"
                :disabled="disabled"
                input-type="textarea"
                :label="$t('Tenders.Category')"
                :max-multiple-count="null"
                :model.sync="categories"
                multiple
                @update:model="updateCategory"/>
            </div>
            <div
              v-if="state === 'error'"
              class="sFormItem__error">
              {{ $t(error) }}
            </div>
          </template>
        </sFormItem>
      </CollapseBlock>
      <CollapseBlock
        transparent
        :value.sync="show.files">
        <div
          slot="title"
          class="group-accred-information__collapse-title mt-0">
          {{ $t('Tenders.Documents') | firstUpperCase }}
          <sIcon
            class="ml-2 my-auto"
            :rotate="show.files ? '-180' : '0'"
            size="14"
            type="arrow"/>
        </div>
        <div>
          <template v-if="loading">
            <v-skeleton-loader
              height="100%"
              loading
              type="image"
              width="100%"/>
          </template>
          <ImportBlock
            v-else
            :create-file-handler="createFileHandler"
            data-qa="files"
            :disabled="!!loading || disabled"
            :external-delete="true"
            file_size="5"
            :files="filesMutable"
            files-types
            inline-style="width: 100%"
            :multiple="true"
            :new-endpoints="true"
            :preview="false"
            @deleteFile="deleteFile"
            @restoreFile="restoreFile"
            @update:files="filesMutable = $event"/>
        </div>
      </CollapseBlock>
      <CollapseBlock
        v-if="$can('show', 'useAccreditationDocumentRequirements')"
        transparent
        :value.sync="show.requirements">
        <div
          slot="title"
          class="group-accred-information__collapse-title mt-0">
          {{ $t('groupOfAccreditation.requirements') }}
          <sIcon
            class="ml-2 my-auto"
            :rotate="show.requirements ? '-180' : '0'"
            size="14"
            type="arrow"/>
        </div>
        <GroupOfAccreditationRequirements
          :disabled="disabled"
          is-create
          is-edit
          :loading="loading"
          :requirements.sync="requirements"/>
      </CollapseBlock>
    </div>
  </div>
</template>
<script>
import CollapseBlock from 'components/common/CollapseBlock.vue'
import ImportBlock from 'components/common/blocks/ImportBlock.vue'
import CategoryDropdownTree from 'components/common/dialogs/CategoryDropDownTree.vue'
import GroupOfAccreditationRequirements from 'components/group of accreditations/create_edit/GroupOfAccreditationRequirements.vue'

import accreditationOrganizationIdMixin from 'mixins/accreditationOrganizationIdMixin.js'
import { StorageFile } from '@/types/commonFile.js'
import { mapGetters } from 'vuex'

export default {
  name: 'GroupOfAccreditationStepInfo',
  mixins: [accreditationOrganizationIdMixin],
  data() {
    return {
      organizationQuery: '',
      show: {
        info: true,
        classificationCode: true,
        files: true,
        requirements: true,
      },
    }
  },
  methods: {
    restoreFile(props) {
      const result = this.$_.cloneDeep(this.filesMutable)
      result[props.n].deleted = false
      result[props.n].error = false
      this.filesMutable = result
    },
    deleteFile(props) {
      const result = this.$_.cloneDeep(this.filesMutable)
      if (props.file.fileId) {
        result[props.n].deleted = true
      } else {
        result.splice(props.n, 1)
      }
      this.filesMutable = result
    },
    createFileHandler(file) {
      return new StorageFile(this.item.id || 'new_item_id', file, 'Accreditation')      
    },
    deleteClassificationCode(index) {
      const result = this.$_.cloneDeep(this.categories)
      result.splice(index, 1)
      this.categories = result?.map(i => ({classificationCode: i.classificationCode, title: i.title})) || []
    },
    filterOrganizations(query) {
      this.organizationQuery = query || ''
    },
    loadOrganizations() {
      this.$store.dispatch('availableOrganizationsForCurrently')
        .catch(error => this.$log_error(error))
    },
    updateCategory(val) {
      this.categories = val.map(i => ({classificationCode: i.classificationCode, title: i.title}))
    },
  },
  computed: {
    ...mapGetters({
      availableOrganizations: 'getAvailableOrganizationsForCurrently',
    }),
    isNewItem() {
      return !!this.itemMutable && !this.itemMutable.id
    },
    showOrganizations() {
      return !this.loading && this.isNewItem && (this.availableOrganizations?.length || 0) > 1
    },
    filteredOrganizations() {
      const query = this.organizationQuery.trim().toLowerCase()
      // Організація без ключа в активному просторі id не може бути надіслана (GraphQL [Int!])
      const organizations = (this.availableOrganizations || []).filter(organization => this.$_accreditationOrganizationKey(organization) != null)
      if (!query) {
        return organizations
      }
      return organizations.filter(organization =>
        organization.title?.toLowerCase().includes(query) || organization.oKPO?.toString().includes(query)
      )
    },
    organizationIds: {
      get() {
        return this.itemMutable?.organizationIds || []
      },
      set(value) {
        this.$set(this.itemMutable, 'organizationIds', value)
      }
    },
    categories: {
      get() {
        return this.itemMutable?.categories || []
      },
      set(value) {
        this.$set(this.itemMutable, 'categories', value)
      }
    },
    requirements: {
      get() {
        return this.itemMutable?.requirements || []
      },
      set(value) {
        this.$set(this.itemMutable, 'requirements', value)
      }
    },
    itemMutable: {
      get() {
        return this.item
      },
      set(newValue) {
        this.$nextTick(() => {
          this.$emit('update:item', newValue)
        })
      }
    },
    filesMutable: {
      get() {
        return this.item.files || []
      },
      set(newValue) {
        this.$nextTick(() => {
          const result = this.$_.cloneDeep(this.item)
          result.files = newValue
          this.itemMutable = result
        })
      }
    }
  },
  filters: {
    firstUpperCase(val) {
      return val[0].toUpperCase() + val.slice(1)
    }
  },
  components: {
    ImportBlock,
    CollapseBlock,
    CategoryDropdownTree,
    GroupOfAccreditationRequirements,
  },
  created() {
    if (!this.$route?.params?.id) {
      this.loadOrganizations()
    }
  },
  props: {
    item: Object,
    loading: Boolean,
    disabled: {
      type: Boolean,
      default: false,
    },
  },
}
</script>
<style scoped lang="scss">
.is-error-border {
  border: 2px solid var(--color-error) !important;
  border-radius: 6px;
}
.group-accred-information {
  max-width: 805px;
  margin: 0 auto;
  display: grid;
  min-height: 100%;
  grid-template-rows: auto 1fr;
  &_content {
    background-color: var(--v-main__light_green-base);
    border-radius: 8px;
    padding: 0 30px 30px;
    overflow: auto;
    min-height: 100%;
    &-item {
      width: 400px;
      text-align: left;
      &-title {
        font-weight: 500;
        font-size: 17px;
        line-height: 22px;
        letter-spacing: 0.17px;
        padding: 24px 24px 24px 0;
        margin-top: 24px;
      }
    }
  }
  &__collapse-title {
    text-align: left;
    padding: 24px 0;
    font-size: 17px;
    font-style: normal;
    font-weight: 500;
    line-height: 22px;
    letter-spacing: 0.17px;
    display: grid;
    grid-template-columns: auto 20px;
    gap: 5px;
  }
}
.classification-item {
  &_title {
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>